<template>
    <div class="container-fluid" v-if="isDataReady" :key="customer.id">
        <v-title :breadcrumb="breadcrumb">
            <span v-fs-block>{{customer.displayName}}</span>
            <div slot="actions" class="display-flex">
                <customer-instruments class="inline-s"/>
                <customer-actions/>
            </div>
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <customer-summary class="stack-m"/>
                <customer-tags/>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="tile tile-secondary">
                    <div class="tile-content">
                        <customer-stats/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full">
                <div class="tile">
                    <div class="tile-content cursor-pointer padding-top-m padding-bottom-m" @click="toggleSegment">
                        <div class="display-flex">
                            <v-toggle
                                class="inline-m no-flex"
                                :selected="true"
                                @input="toggleSegment"
                                :value="showSegment"
                            />
                            <p>Show Segments: <span class="text-muted">Invoices, Transactions, and Logs</span></p>
                        </div>
                    </div>
                    <div class="tile-content customer-details-segments" v-if="showSegment">
                        <div class="grid">
                            <div class="grid-item segments-column no-flex" v-show="isColumnOpen">
                                <segments-column :page="page"/>
                            </div>
                            <div class="grid-item segments-grid">
                                <segment-grid v-if="activeSegment"/>
                                <template v-else>
                                    <div class="tile">
                                        <div class="tile-content">
                                            <v-no-content slot="no-content">
                                                <span slot="title">It seems that you have not selected a Segment</span>
                                                <span slot="description">Please select a segment.</span>
                                            </v-no-content>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <timeline
                    ref="timeline"
                    type="customer"
                    :key="`customer-${customer.id}-timeline`"
                    :entity="customer"/>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="grid">
                    <template v-if="$auth.upcomingInvoices.isAllowed">
                        <customer-subscriptions active @refresh-subscriptions="refreshSubscriptions"/>
                        <customer-subscriptions pending @refresh-subscriptions="refreshSubscriptions"/>
                        <customer-subscriptions past @refresh-subscriptions="refreshSubscriptions"/>
                        <customer-subscriptions oneTime @refresh-subscriptions="refreshSubscriptions"/>
                    </template>
                    <div class="grid-item grid-item-full" v-if="$auth.leadSources.edit.isAllowed">
                        <lead-sources
                            @updated="fetchTimeline"
                            :entry="customer"/>
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.kyc.isAllowed && isMgsMember">
                        <kyc-manager
                            :customer="customer"
                            @updated="fetchTimeline"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters, mapState} from 'vuex';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import vTitle from '@/components/v-title';
    import vNoContent from '@/components/v-no-content';
    import {
        vIcon, vIconButton, vToggle,
    } from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import LeadSources from '@/components/lead-sources/lead-sources';
    import KycManager from '@/components/kyc/kyc-manager';
    import SegmentsColumn from '@/modules/segments/components/segments-column';
    import SegmentDataModel from '@/common/models/segment-data-model';
    import SegmentGrid from '@/modules/segments/components/segment-grid';
    import CustomerSummary from '@/modules/customers/components/customer-summary';
    import CustomerActions from '@/modules/customers/components/customer-actions';
    import CustomerInstruments from '@/modules/customers/components/customer-instruments';
    import CustomerStats from '@/modules/customers/components/customer-stats';
    import CustomerTags from '@/modules/customers/components/customer-tags';
    import truncate from '@/common/helpers/truncate';

    export default {
        mixins: [LoadStateMixin],
        name: 'customer-details-new',
        components: {
            vNoContent,
            vTitle,
            SegmentsColumn,
            vIcon,
            vToggle,
            vInlineLoader,
            vIconButton,
            LeadSources,
            KycManager,
            CustomerSummary,
            CustomerTags,
            CustomerActions,
            CustomerInstruments,
            CustomerStats,
            SegmentGrid,
            Timeline: () => import('@/components/timeline/timeline.vue'),
            CustomerSubscriptions: () => import('@/modules/customers/components/customer-subscriptions'),
        },
        props: {
            id: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                page: SegmentDataModel.Pages.customerDetails,
                isLoadingSubscriptions: false,
                isLoadingCoupons: false,
                coupons: null,
                reloadOnParam: 'id',
            };
        },
        computed: {
            ...mapState('customers', ['customer', 'contacts']),
            ...mapState('segments', ['activeFilters']),
            ...mapGetters({
                activeSegment: 'segments/activeSegment',
                activeOrders: 'customersOrders/activeOrders',
                pendingOrders: 'customersOrders/pendingOrders',
                pastOrders: 'customersOrders/pastOrders',
            }),
            isMgsMember() {
                return this.$store.getters['user/isMgsMember'];
            },
            isColumnOpen() {
                // TODO consider using a transition effect
                return this.$store.state.segments.isSegmentsColumnOpen;
            },
            showSegment() {
                return this.$store.state.customers.isSegmentPanelOpen;
            },
            breadcrumb() {
                return {
                    label: truncate(this.customer.displayName, {dots: true, limit: 36}),
                    route: {
                        name: 'customer-details',
                        params: {
                            id: this.customer.id,
                        },
                    },
                };
            },
        },
        methods: {
            ...mapActions({
                setCustomer: 'customers/set',
                fetchContacts: 'customers/fetchCustomerContacts',
                fetchCustomer: 'customers/fetch',
                fetchSegments: 'segments/fetch',
                activateSegmentPage: 'segments/activateSegmentPage',
                fetchCustomerOrders: 'customersOrders/fetchCustomerOrders',
                setSegmentPanelOpen: 'customers/setSegmentPanelOpen',
            }),
            toggleSegment() {
                this.setSegmentPanelOpen(!this.showSegment);
            },
            fetchTimeline() {
                this.$refs.timeline.update();
            },
            async fetchData() {
                const customerId = this.id;
                const customerParams = {
                    id: customerId,
                    expand: 'leadSource,defaultPaymentInstrument',
                };
                try {
                    await this.fetchSegments(this.$auth);
                    this.activateSegmentPage(SegmentDataModel.Pages.customerDetails);
                    await Promise.all([
                        this.fetchCustomer({params: customerParams}),
                        this.fetchCustomerOrders({params: customerId}),
                    ]);
                    this.fetchContacts();
                } catch (error) {
                    this.$toast.fromError(error, 'Unable retrieve all the customer data. Please try again later or contact support.');
                    this.$redirectOnError(error, {name: 'lists'});
                }
            },
            async refreshSubscriptions() {
                this.isLoadingSubscriptions = true;
                const customerId = this.id;
                // TODO Updating a resource than getting a collection of that same resource, causes issues with Elasticsearch
                setTimeout(async () => {
                    try {
                        await this.fetchCustomerOrders({params: customerId});
                    } catch (error) {
                        this.$toast.fromError(error, 'Unable to refresh subscriptions, please try again or contact support');
                    } finally {
                        this.isLoadingSubscriptions = false;
                    }
                }, 1000);
            },
        },
    };
</script>
